<template>
	<div class="container">
		<div class="page page-inventory">
			<!-- Header -->
			<div class="page-header d-flex justify-content-between">
				<h1 class="page-title">{{ $t('Credit Detail') }}</h1>
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>

			<!-- Overviews -->
			<div class="inventory-summaries row">
				<div class="col-md-3">
					<div class="box box--primary mh-120">
						<div class="m-2">
							<h4
								class="text-success font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingBalance }">
								<span> {{ balanceDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('Current Balance') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingWithdrawals }">
								<span> {{ withdrawnDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('On Process Withdrawal') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingStash }">
								<span> {{ stashDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Stash Balance') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingWithdrawals }">
								<span> {{ withdrawalDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Completed Withdrawal') }}</span>
						</div>
					</div>
				</div>
			</div>
			<hr />
			<div class="row">
				<div class="col-md-7 text-right">
					<div class="form-group">
						<div class="d-flex justify-content-start">
							<div class="input mr-3">
								<multiselect v-model="selectedSupplier"
								:show-labels="false"
								track-by="_id"
								label="label"
								placeholder="email/nama supplier"
								:options=optionSupplier
								@search-change="fetchDataSupplier"
								>
								<span slot="noResult">
									No options matching the query
								</span>
								<span slot="noOptions">
									No options available
								</span>
							</multiselect>
							</div> <!-- Close the div tag here -->
							<div class="input">
								<input type="text" class="input form-control" placeholder="Search Order ID" v-model="orderId" @input="onKeywordChange">
							</div> <!-- Close the div tag here -->
						</div>
					</div>
				</div>
				<div class="mb-4 col-md-5 text-right ml-a">
					<div class="d-flex justify-content-start">
						<!-- Export -->
						<button
							type="button"
							class="btn btn-sm btn-primary w-100"
							:class="{ 'is-loading': isExporting }"
							@click="exportCredit"
							>
							<i class="material-icons">save_alt</i>
							<span class="mr-2">{{ $t('Export') }}</span>
						</button>
						<select class="form-control mx-2" v-model="typeSelected">
							<option value="all">All Type</option>
							<option value="withdraw">Withdraw</option>
							<option value="outsource_payment_product">Revenue</option>
							<option value="handling_fee">Handling Fee</option>
							<option value="payment">Other</option>
							<!-- outsource_commission, payment,   -->
						</select>
						<select class="form-control" v-model="statusSelected">
							<option value="all">All Status</option>
							<option value="pending">Pending</option>
							<option value="processing">Processing</option>
							<option value="completed">Completed</option>
							<option value="cancelled">Canceled</option>
						</select>
						<!-- <div class="col-md-6">
						</div>
						<div class="col-md-6">
						</div> -->
					</div>
				</div>
			</div>

			<!-- Main Table -->
			<div class="card card-body border-0">
				<div class="wallet-history-table table-responsive">
					<table class="table table-striped table-hover">
						<thead>
							<tr>
								<th width="5%">{{ $t('No') }}.</th>
								<th width="5%">{{ $t('Order Id') }}</th>
								<th width="30%">{{ $t('Description') }}</th>
								<th width="20%">{{ $t('Value') }}</th>
								<th width="12%">{{ $t('Status') }}</th>
								<th>{{ $t('Type') }}</th>
								<th width="30%">{{ $t('Date') }}</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<template
								v-for="(act, index) in histories">
								<tr
									class="cur-p"
									:class="{ 'highlighted': history && history.id === act.id }"
									:key="`${index}-1`"
									@click="showDetails(act)">
									<!-- Index -->
									<td class="text-right">{{ index + 1 }}.</td>

									<!-- Order Id -->
									<td>
										<span class="text-bold text-primary" v-if="act && act.order_id !== '' && act.order_id !== null">
											#{{ act.order_id }}
										</span>
										<span v-else>-</span>
									</td>

									<!-- Description -->
									<td>
										<span v-if="act.description && act.description !== ''">
											{{ act.description }}
										</span>
										<span v-if="act.description === ''">-</span>
									</td>

									<!-- Value -->
									<td class="text-right">
										<mutation-value :data="act" :key="act.id" />
									</td>

									<!-- Changed by -->
									<!-- <td>
										<span>{{ getMemberName(act.created_by) }}</span>
									</td> -->

									<!-- Status -->
									<td class="text-center">
										<mark :class="`order-status status-${act.status}`"><span>{{ act.status }}</span></mark>
									</td>

									<!-- Type -->
									<td class="text-left">
										<span>{{ getTypeLabel(act.type) }}</span>
									</td>

									<!-- Date -->
									<td class="text-center">
										<span>{{ formatDate(act.created_at) }}</span>
									</td>

									<!-- Actions -->
									<td class="text-center">
										<div class="d-flex align-items-center justify-content-end pr-4">
											<wallet-history-metas
												class="mr-3"
												:show-title="false"
												:metas="act.metas"
												v-if="act.metas"
											/>
											<i class="material-icons cur-p" v-tooltip="'Show Details'" v-if="act.type !== 'withdraw'">
												<template v-if="history && history.id === act.id">keyboard_arrow_up</template>
												<template v-else>keyboard_arrow_down</template>
											</i>
										</div>
									</td>
								</tr>
								<tr
									class="detail-view"
									:key="`${index}-2`"
									v-if="history && history.id === act.id && history.type !== 'withdraw'">
									<td colspan="7">
										<template v-if="act.details">
											<div
												class="text-center"
												v-if="act.details.length === 0">
												- {{ $t('No Commission Details') }} -
											</div>
											<!-- Details -->
											<div class="p-1" v-else>
												<h6 class="mb-3 text-bold">{{ $t('Commission Details') }}</h6>
												<div class="card card-body p-3">
													<wallet-history-details :details="act.details" />
												</div>
											</div>
										</template>
										<wallet-history-metas :metas="act.metas" v-if="act.metas" />
										<div class="is-loading dark" v-if="!act.details || !act.metas"></div>
									</td>
								</tr>
							</template>
						</tbody>
					</table>
				</div>
			</div>

			<!-- Table Loader -->
			<div
				class="table-fetch-status"
				v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Table Empty -->
			<div
				class="list-empty border-0"
				v-if="histories.length === 0 && isFetching === false">
				<span><i class="material-icons md-48 mr-2">error_outline</i></span>
				<span v-if="keyword === ''">There is no data in this date range.</span>
				<span v-else>No activities was found</span>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import WalletHistoryDetails from '@/components/WalletHistoryDetails.vue';
import WalletHistoryMetas from '@/components/WalletHistoryMetas.vue';
import MutationValue from '@/components/MutationValue.vue';
import {
	duplicateVar,
	getDateRange,
	getAxiosErrorMessage,
	formatNumberPlain,
	sensorEmailAddress,
	toTitleCase,
	downloadFile,
} from '@/lib/helper';
import walletApi from '@/api/wallet';
import adminApi from '@/api/admin';
import walletStashApi from '@/api/wallet-stash';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminCreditDetail',
	components: {
		Daterangepicker,
		WalletHistoryDetails,
		WalletHistoryMetas,
		MutationValue,
	},
	data() {
		return {
			keyword: '',
			keywordOrderId: '',
			limit: 25,
			page: 1,
			sort_by: 'id',
			sort: 'desc',
			histories: [],
			history: null,
			isFetching: false,
			isExporting: false,
			isAll: false,
			filterDate: defaultFilterDate,
			isFetchingBalance: false,
			balance: 0,
			withdrawn: 0,
			withdrawTreshold: 0,
			bankAccount: null,
			password: '',
			stashTotal: 0,
			isFetchingStash: false,
			selectedSupplier: null,
			optionSupplier: [],
			isSearchingSupplier: false,
			typeSelected: 'all',
			statusSelected: 'all',
			orderId: '',
			overviews: {
				withdrawal: 0,
			},
			isFetchingWithdrawals: false,
		};
	},
	methods: {
		...mapActions({
		}),
		initSelected() {
			this.selectedSupplier = {
				email: this.email,
				label: this.email,
			};
		},
		resetHistories() {
			this.histories = [];
			this.isAll = false;
			this.page = 1;
		},
		fetchHistories(reset = true, includeDate = true) {
			this.isFetching = true;
			if (reset) {
				this.resetHistories();
			}
			const params = this.getFetchParams(includeDate);
			const callback = (response) => {
				const histories = response.data.data;
				histories.forEach((history) => {
					this.histories.push(history);
				});
				this.page++;
				if (histories.length < this.limit) {
					this.isAll = true;
				}
				this.isFetching = false;
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			walletApi.getHistories(params, callback, errorCallback);
		},
		init() {
			this.initSelected();
			this.initFilterDate();
			this.getBalance();
			this.getOnProcessWithdrawal();
			this.getStashBalance();
			this.fetchOverviews();
		},
		refresh() {
			this.getBalance();
			this.getOnProcessWithdrawal();
			this.getStashBalance();
			this.fetchOverviews();
			this.fetchHistories();
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.refresh();
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				if (this.selectedSupplier !== '') {
					self.fetchHistories(true, false);
				} else {
					self.fetchHistories(true);
				}
			}, 1000);
		},
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchHistories(false, false);
				}
			}
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchHistories(true, false);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
		checkCapabilities() {
			if (!this.isOwner && !this.isUserCan('wallet_view')) {
				this.$router.push('/');
			} else {
				this.isRouteChecked = true;
			}
		},
		formatDate(dateString) {
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = this.user.timezone;
			const formattedDate = this.moment.utc(dateString).tz(timezone).format(format);
			return dateString && dateString !== '' ? formattedDate : '';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const format = 'YYYY-MM-DD';
				const since = dayjs(this.since).format(format);
				const until = dayjs(this.until).format(format);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		getFetchParams(includeDate = true) {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				type: this.typeSelected,
				status: this.statusSelected,
			};

			const selectedSupplier = this.selectedSupplier;
			const keywordOrderId = this.orderId.trim();

			if (selectedSupplier && selectedSupplier !== '') {
				params.email = this.selectedSupplier.email;
			}

			if (keywordOrderId && keywordOrderId !== '') {
				params.order_id = keywordOrderId;
			}

			if (includeDate) {
				params.since = startDate;
				params.until = endDate;
			}

			return params;
		},
		getBalance() {
			this.isFetchingBalance = true;
			const callback = (response) => {
				this.isFetchingBalance = false;
				const wallet = response.data;
				this.balance = wallet.balance;
			};
			const errorCallback = (e) => {
				this.isFetchingBalance = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
			};

			let params = {};
			if (this.email) {
				params = {
					email: this.email,
				};
			}
			walletApi.getBalance(callback, errorCallback, params);
		},
		formatNumber(number) {
			return formatNumberPlain(number);
		},
		showDetails(history) {
			if (this.history && this.history.id === history.id) {
				this.history = null;
				return;
			}
			this.history = duplicateVar(history);
			if (!history.details) {
				const callback = (response) => {
					const historyItem = response.data;
					this.history = historyItem;
					this.updateHistory(historyItem);
				};
				const errorCallback = () => {};
				walletApi.getHistory(history.id, callback, errorCallback);
			}
		},
		getTypeLabel(type) {
			let label = type;
			if (type === 'cod_revenue') {
				label = 'COD Revenue';
			} else {
				label = toTitleCase(type.replace(/_/g, ' '));
			}
			return label;
		},
		getStashBalance() {
			this.isFetchingStash = true;
			const callback = (response) => {
				this.isFetchingStash = false;
				const total = response.data;
				this.stashTotal = total;
			};
			const errorCallback = (e) => {
				this.isFetchingStash = false;
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Stash Balance',
					text: message,
				});
			};

			let params = {};

			if (this.email) {
				params = {
					email: this.email,
				};
			}
			walletStashApi.getTotal(callback, errorCallback, params);
		},
		fetchDataSupplier(query) {
			this.isSearchingSupplier = true;

			const params = {
				keyword: query,
				role: 'owner',
				owner_type: 'supplier',
			};

			const callback = (response) => {
				this.optionSupplier = response.data;
				this.isSearchingSupplier = false;
			};
			const errorCallback = () => {
				this.isSearchingSupplier = false;
			};

			this.ooDelay(() => {
				if (query !== '') {
					adminApi.searchUser(params, callback, errorCallback);
				}
			}, 1000);
		},
		getOnProcessWithdrawal() {
			this.withdrawn = 0;
			this.isFetchingWithdrawals = true;
			const callback = (response) => {
				const total = response.data;
				this.withdrawn = total;
				this.isFetchingWithdrawals = false;
			};
			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Balance',
					text: message,
				});
				this.isFetchingWithdrawals = false;
			};

			let params = {};

			if (this.email) {
				params = {
					email: this.email,
				};
			}

			walletApi.getOnProcessWithdrawal(callback, errorCallback, params);
		},
		fetchOverviews() {
			this.isFetchingWithdrawals = true;
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				since: startDate,
				until: endDate,
			};

			if (this.email) {
				params.email = this.email;
			}

			const callback = (response) => {
				this.isFetchingWithdrawals = false;
				const overviews = response.data;
				this.overviews = overviews;
			};
			const errorCallback = () => {
				this.isFetchingWithdrawals = false;
			};
			walletApi.getOverviews(params, callback, errorCallback);
		},
		exportCredit() {
			this.isExporting = true;
			const dateFormat = 'YYYY-MM-DD';
			const startDate = dayjs(this.filterDate.start).format(dateFormat);
			const endDate = dayjs(this.filterDate.end).format(dateFormat);
			const params = {
				sort_by: this.sort_by,
				sort: this.sort,
				since: startDate,
				until: endDate,
			};
			if (this.typeSelected) {
				params.type = this.typeSelected;
			}
			if (this.statusSelected !== 'all') {
				params.status = this.statusSelected;
			}
			if (this.email) {
				params.email = this.email;
			}
			const callback = (response) => {
				const url = response.data;
				if (url && url !== '') {
					downloadFile(url);
				} else {
					const message = 'No Credit with the selected type';
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export Credit',
						text: message,
					});
				}
				this.isExporting = false;
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			walletApi.exportHistories(params, callback, errorCallback);
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			isAdmin: 'isAdmin',
			isOwner: 'isOwner',
			isUserCan: 'isUserCan',
			isHasFeature: 'isHasFeature',
			isTester: 'isTester',
			bankAccounts: 'bankAccounts',
			presetRangeLabel: 'presetRangeLabel',
			filterDatePresets: 'filterDatePresets',
			members: 'members/all',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		balanceDisplay() {
			return formatNumberPlain(this.balance);
		},
		withdrawnDisplay() {
			return formatNumberPlain(this.withdrawn);
		},
		withdrawalDisplay() {
			return formatNumberPlain(this.overviews.withdrawal);
		},
		stashDisplay() {
			return formatNumberPlain(this.stashTotal);
		},
		isCanWithdraw() {
			return this.isOwner || this.isUserCan('wallet_withdraw');
			// return false;
		},
		sensoredEmail() {
			const email = this?.user?.email ?? '';
			const sensoredEmail = this.isOwner ? sensorEmailAddress(email) : 'Owner account ini';
			return sensoredEmail;
		},
		email() {
			return this.$route.query.user_email;
		},
		isWdDisabled() {
			return this.isHasFeature('withdraw_disabled');
		},
	},
	watch: {
		user() {
			this.init();
		},
		selectedSupplier() {
			if (this.selectedSupplier) {
				const query = { ...this.$route.query };
				query.user_email = this.selectedSupplier.email;

				this.$router.push({
					path: this.$route.path,
					query,
				});
				this.refresh();
			}
		},
		typeSelected() {
			if (this.typeSelected) {
				this.refresh();
			}
		},
		statusSelected() {
			if (this.statusSelected) {
				this.refresh();
			}
		},
		email() {
			this.refresh();
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
		window.addEventListener('scroll', this.handleScroll);
		document.addEventListener('scroll', this.onDocumentScroll, true);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
		document.removeEventListener('scroll', this.onDocumentScroll, true);
	},
};
</script>

	<style scoped>
	.mh-120 {
		min-height: 120px;
	}
	</style>
